import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';

/**
 * Resposta esperada do endpoint GET /metric/dashboard-summary/:userId
 *
 * O back deve retornar:
 * {
 *   success: boolean,
 *   data: {
 *     plays_today: number,          // total de plays do dia corrente
 *     views_today: number,          // total de visualizações únicas do dia
 *     avg_retention_today: number,  // retenção média (%) do dia
 *     peak_hour_today: string,      // horário de pico, ex: "14:00"
 *     conversions_today: number,    // conversões registradas hoje
 *     hourly: Array<{               // distribuição horária (24 pontos ou menos)
 *       hour: number,               // 0-23
 *       plays: number,
 *       views: number,
 *       conversions: number,
 *     }>
 *   }
 * }
 */

export interface DashboardMetricsSummary {
  plays_today: number;
  views_today: number;
  avg_retention_today: number;
  peak_hour_today: string;
  conversions_today: number;
  hourly: Array<{
    hour: number;
    plays: number;
    views: number;
    conversions: number;
  }>;
}

/**
 * Payload do endpoint acrescido do play rate derivado no front.
 * O back ainda não devolve esse campo — ele é calculado no `select` da query.
 */
export interface DashboardMetricsWithPlayRate extends DashboardMetricsSummary {
  /** Plays sobre visualizações únicas do dia, em percentual (0-100). */
  play_rate_today: number;
}

interface DashboardMetricsResponse {
  success: boolean;
  data: DashboardMetricsSummary;
}

/**
 * Play rate em percentual (0-100).
 *
 * `plays` conta eventos com play=true e `views` conta eventos com uniqueView=true,
 * então a razão NÃO é limitada a 1 (um visitante recorrente gera play sem uniqueView).
 * Daí o teto de 100 — o card não pode exibir "112%".
 */
export function calcPlayRate(plays: number, views: number): number {
  if (!views || views <= 0) return 0;
  return Math.min(100, Math.round((plays / views) * 100));
}

export function useDashboardMetrics(userId: string | undefined) {
  return useQuery<DashboardMetricsSummary | null, Error, DashboardMetricsWithPlayRate | null>({
    queryKey: ['dashboard-metrics', userId],
    queryFn: async () => {
      if (!userId) return null;
      const response = await api.get<DashboardMetricsResponse>(
        `/metric/dashboard-summary/${userId}`
      );
      return response?.data ?? null;
    },
    select: (data) => (
      data
        ? { ...data, play_rate_today: calcPlayRate(data.plays_today, data.views_today) }
        : null
    ),
    enabled: !!userId,
    staleTime: 60 * 1000,        // 1 minuto
    refetchInterval: 60 * 1000,  // revalida a cada 1 minuto
    retry: false,                 // não retentar se endpoint ainda não existe
  });
}
